import { Component, Host, h, Prop, Method, Event, EventEmitter, Listen, Element } from '@stencil/core';
import focusLock from 'dom-focus-lock';
import { disableBodyScroll, enableBodyScroll } from '../../utils/utils';
import { DOMElement } from '../../entities';

@Component({
  tag: 'me-modal',
  styleUrl: 'me-modal.scss',
  shadow: false,
})
export class MeModal {

  private static nextModalLabelID = 1;
  private readonly modalLabelID = `me-modal-label-${MeModal.nextModalLabelID++}`;

  @Element() host: HTMLMeModalElement;

  @Prop() modalTitle: string;
  @Prop({ mutable: true, reflect: true }) active = false;

  @Event() meModalOpen: EventEmitter<HTMLMeModalElement>;
  @Event() meModalClose: EventEmitter<HTMLMeModalElement>;

  private isAnimated = false;
  private isFocusLockActive = false;
  private scrollContainer?: HTMLElement;

  private get shouldOpenOnPageLoad() {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('me-modal') === this.host.id;
  }

  @Listen('click', { target: 'window' })
  async handleWindowClick(event: PointerEvent) {
    if (!(event.target instanceof DOMElement)) { return; }

    const toggle = event.target.closest(`[data-me-modal-toggle="${this.host.id}"]`);

    if (!(toggle instanceof HTMLElement)) { return; }

    const shouldToggle =
      !('meModalToggleIf' in toggle.dataset)
      || Boolean(window[toggle.dataset.meModalToggleIf]?.call());

    if (shouldToggle) {
      await this.toggle();
    }
  }

  @Listen('meBackdropClose', { target: 'window' })
  async handleBackdropClose() {
    await this.close();
  }

  @Listen('transitionend')
  handleTransitionEnd(event) {
    if (event.target !== this.host) { return; }

    this.manageFocus();
  }

  async componentDidLoad() {
    this.isAnimated = true;

    if (this.active || this.shouldOpenOnPageLoad) {
      await this.open(true);
    }
  }

  async disconnectedCallback() {
    focusLock.off(this.host);
  }

  render() {
    const classList = [
      'me-modal',
      this.isAnimated ? 'me-modal--animated' : null,
      this.active ? 'me-modal--active' : null,
    ].filter(c => !!c).join(' ');

    return (
      <Host class={classList} role="dialog" aria-labelledby={this.modalLabelID}>
        <div class="me-modal__header">
          <div id={this.modalLabelID} class="me-modal__title">
            {this.modalTitle}
          </div>
          <me-button
            class="me-modal__close-button"
            variant="link"
            onClick={this.close.bind(this)}
          >
            <i class="fa fa-xmark fa-lg" aria-label="Schließen"></i>
          </me-button>
        </div>
        <div class="me-modal__scroll-container" ref={el => this.scrollContainer = el}>
          <div class="me-modal__body">
            <slot></slot>
          </div>
        </div>
      </Host>
    );
  }

  @Method()
  async toggle() {
    this.active
      ? await this.close()
      : await this.open();
  }

  @Method()
  async open(forced = false) {
    if ((this.active && !forced) || !this.scrollContainer) { return; }

    disableBodyScroll(this.scrollContainer, { reserveScrollBarGap: true });

    this.active = true;

    this.meModalOpen.emit(this.host);
  }

  @Method()
  async close() {
    if (!this.active || !this.scrollContainer) { return; }

    enableBodyScroll(this.scrollContainer);

    this.active = false;

    this.meModalClose.emit(this.host);
  }

  /**
   * Manages focus
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/dialog_role#required_javascript_features
   */
  private manageFocus() {
    if (this.active && !this.isFocusLockActive) {
      this.isFocusLockActive = true;
      focusLock.on(this.host);
    } else if (!this.active && this.isFocusLockActive) {
      focusLock.off(this.host);
      this.isFocusLockActive = false;
    }
  }

}
